import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabase';

// Background photo upload function (non-blocking)
const uploadPhotoInBackground = async (studentId, photo, studentIdText) => {
  try {
    const fileExt = photo.name.split('.').pop();
    const fileName = `student-${studentIdText}-${Date.now()}.${fileExt}`;
    
    console.log('Starting background photo upload for student:', studentId);
    
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('student-photos')
      .upload(fileName, photo);

    if (uploadError) {
      console.error('Background photo upload error:', uploadError);
      return;
    }

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('student-photos')
      .getPublicUrl(fileName);
    
    // Update student record with photo URL
    const { error: updateError } = await supabase
      .from('students')
      .update({ photo_url: publicUrl })
      .eq('id', studentId);

    if (updateError) {
      console.error('Error updating student with photo URL:', updateError);
    } else {
      console.log('Photo uploaded and student updated successfully in background:', publicUrl);
    }
  } catch (photoError) {
    console.error('Background photo upload failed:', photoError);
  }
};

// Background course assignment insertion function (non-blocking)
const insertCourseAssignmentsInBackground = async (studentId, courseAssignments, academicYear) => {
  try {
    const validAssignments = courseAssignments.filter(assignment => 
      assignment.moduleId && assignment.year && assignment.semester && assignment.type && assignment.status
    );
    
    console.log('Background course assignments to be inserted:', validAssignments.length);
    console.log('Valid assignments:', validAssignments);

    if (validAssignments.length === 0) {
      console.log('No valid course assignments to insert in background');
      return;
    }

    const enrollmentData = validAssignments.map(assignment => ({
      student_id: studentId,
      module_id: assignment.moduleId,
      academic_year: academicYear,
      semester: parseInt(assignment.semester),
      enrollment_type: assignment.type.toLowerCase(),
      enrolled_date: new Date().toISOString(),
      active: assignment.status === 'Active',
      created_at: new Date().toTimeString().split(' ')[0] + '+00:00' // Format as HH:MM:SS+00:00
    }));

    console.log('Inserting course enrollment data in background:', enrollmentData);
    
    const { data: insertedEnrollments, error: enrollmentError } = await supabase
      .from('student_module_enrollments')
      .insert(enrollmentData)
      .select();

    if (enrollmentError) {
      console.error('Background error creating course enrollments:', enrollmentError);
      console.error('Enrollment data that failed:', enrollmentData);
    } else {
      console.log('Course assignments created successfully in background:', insertedEnrollments);
    }
  } catch (error) {
    console.error('Background course assignment insertion failed:', error);
  }
};

const AddStudentForm = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // Mobile responsiveness state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1024);

  // Helper function for responsive input/select styles
  const getInputStyles = () => ({
    width: '100%',
    height: isMobile ? 54 : 49,
    padding: isMobile ? '0 16px' : '0 15px',
    borderRadius: 8,
    border: '1px solid #77838F',
    fontSize: 16,
    fontFamily: 'Outfit',
    minHeight: '48px',
    WebkitAppearance: 'none',
    boxSizing: 'border-box'
  });

  const getLabelStyles = () => ({
    display: 'block',
    marginBottom: '8px',
    color: 'black',
    fontSize: isMobile ? 18 : 20,
    fontFamily: 'Outfit',
    fontWeight: '500'
  });

  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    studentId: '',
    contactNumber: '',
    dateOfBirth: '',
    gender: '',
    address: '',
    degreeProgramme: '',
    selectedDegreeId: '', // Store the degree_details ID
    group: '',
    selectedGroupId: '', // Store the group ID
    currentYear: '',
    currentSemester: '',
    enrollmentDate: new Date().toISOString().split('T')[0], // Today's date
    photo: null,
    status: 'Active'
  });

  // Data lists
  const [degreeProgrammes, setDegreeProgrammes] = useState([]);
  const [groups, setGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  // Course assignments state
  const [courseAssignments, setCourseAssignments] = useState([
    {
      id: 1,
      year: '',
      semester: '',
      moduleId: '',
      moduleName: '',
      moduleSearchQuery: '',
      type: '',
      status: 'Active'
    }
  ]);
  const [academicYear, setAcademicYear] = useState('');

  // Module data for dropdowns
  const [modulesList, setModulesList] = useState([]);
  const [filteredModules, setFilteredModules] = useState({});
  const [showModuleDropdowns, setShowModuleDropdowns] = useState({});

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);

        // Get admin's university_id
        let universityId = currentUser?.university_id;
        
        if (!universityId && currentUser?.id) {
          const { data: adminData, error: adminError } = await supabase
            .from('admins')
            .select('university_id')
            .eq('user_id', currentUser.id)
            .single();
          
          if (!adminError && adminData) {
            universityId = adminData.university_id;
          }
        }

        if (!universityId) {
          console.error('Unable to determine university ID');
          return;
        }

        console.log('Loading data for university:', universityId);

        // Load degree programs from Supabase filtered by university_id
        const { data: degreeData, error: degreeError } = await supabase
          .from('degree_details')
          .select('id, degree_programme, local_international, degree_level, active')
          .eq('university_id', universityId)
          .eq('active', true)
          .order('degree_programme', { ascending: true });

        if (degreeError) {
          console.error('Error loading degree programs:', degreeError);
          throw degreeError;
        }

        // Format degree programs for display: "degree_programme (local_international)"
        const formattedDegreePrograms = degreeData?.map(degree => ({
          id: degree.id,
          displayName: `${degree.degree_programme} (${degree.local_international})`,
          originalData: degree
        })) || [];

        console.log(`Loaded ${formattedDegreePrograms.length} degree programs`);
        setDegreeProgrammes(formattedDegreePrograms);

        // Load groups from Supabase filtered by university_id
        const { data: groupData, error: groupError } = await supabase
          .from('groups')
          .select('id, group_name, degree_details_id, year, semester, active')
          .eq('university_id', universityId)
          .eq('active', true)
          .order('group_name', { ascending: true });

        if (groupError) {
          console.error('Error loading groups:', groupError);
          throw groupError;
        }

        console.log(`Loaded ${groupData?.length || 0} groups`);
        setGroups(groupData || []);

        // Load modules from Supabase with degree mappings
        const { data: modulesData, error: modulesError } = await supabase
          .from('modules')
          .select(`
            id, 
            module_name, 
            module_code, 
            semester, 
            active,
            module_degree_mappings!inner(
              year,
              degree_details_id,
              is_mandatory
            )
          `)
          .eq('university_id', universityId)
          .eq('active', true)
          .order('module_name', { ascending: true });

        if (modulesError) {
          console.error('Error loading modules:', modulesError);
          throw modulesError;
        }

        console.log(`Loaded ${modulesData?.length || 0} modules for university ${universityId}`);
        setModulesList(modulesData || []);
        
        // Initialize filtered modules for existing course assignments
        const initialFiltered = {};
        courseAssignments.forEach(assignment => {
          initialFiltered[assignment.id] = modulesData || [];
        });
        setFilteredModules(initialFiltered);
        
      } catch (error) {
        console.error('Error loading data:', error);
        alert('Error loading data: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      loadData();
    }
  }, [currentUser]);

  // Filter groups based on selected degree programme
  useEffect(() => {
    if (formData.selectedDegreeId) {
      const filtered = groups.filter(group => 
        group.degree_details_id === formData.selectedDegreeId
      );
      setFilteredGroups(filtered);
    } else {
      setFilteredGroups([]);
    }
    // Reset group selection when degree programme changes
    setFormData(prev => ({ ...prev, group: '', selectedGroupId: '' }));
  }, [formData.selectedDegreeId, groups]);

  // Update module filtering when degree programme changes
  useEffect(() => {
    if (formData.selectedDegreeId) {
      // Clear all module selections when degree programme changes
      setCourseAssignments(prev => 
        prev.map(assignment => ({
          ...assignment,
          moduleId: '',
          moduleName: '',
          moduleSearchQuery: ''
        }))
      );
      
      // Re-filter modules for all course assignments when degree programme changes
      courseAssignments.forEach(assignment => {
        updateFilteredModulesForAssignment(assignment.id, assignment);
      });
    }
  }, [formData.selectedDegreeId, modulesList]);

  // Close module dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.module-search-container')) {
        setShowModuleDropdowns({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle responsive breakpoints
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Handle degree program selection - store both display name and ID
    if (name === 'degreeProgramme') {
      const selectedDegree = degreeProgrammes.find(degree => degree.displayName === value);
      setFormData(prev => ({
        ...prev,
        selectedDegreeId: selectedDegree ? selectedDegree.id : '',
        degreeProgramme: value
      }));
    }

    // Handle group selection - store both display name and ID
    if (name === 'group') {
      const selectedGroup = filteredGroups.find(group => group.group_name === value);
      setFormData(prev => ({
        ...prev,
        selectedGroupId: selectedGroup ? selectedGroup.id : '',
        group: value
      }));
    }
  };

  // Handle photo upload
  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({
      ...prev,
      photo: file
    }));
  };

  // Handle course assignment changes
  const handleCourseAssignmentChange = (id, field, value) => {
    setCourseAssignments(prev => 
      prev.map(assignment => 
        assignment.id === id 
          ? { 
              ...assignment, 
              [field]: value,
              // Clear module selection if year or semester changes
              ...(field === 'year' || field === 'semester' ? {
                moduleId: '',
                moduleName: '',
                moduleSearchQuery: ''
              } : {})
            }
          : assignment
      )
    );

    // If year or semester changed, update filtered modules for this assignment
    if (field === 'year' || field === 'semester') {
      const assignment = courseAssignments.find(a => a.id === id);
      const updatedAssignment = { ...assignment, [field]: value };
      updateFilteredModulesForAssignment(id, updatedAssignment);
    }
  };

  // Update filtered modules based on year and semester selection
  const updateFilteredModulesForAssignment = (assignmentId, assignment) => {
    let filtered = modulesList;

    // Filter by selected degree programme from main form
    if (formData.selectedDegreeId) {
      filtered = filtered.filter(module => 
        module.module_degree_mappings && 
        module.module_degree_mappings.some(mapping => 
          mapping.degree_details_id === formData.selectedDegreeId
        )
      );
    }

    // Filter by year if selected
    if (assignment.year) {
      filtered = filtered.filter(module => 
        module.module_degree_mappings && 
        module.module_degree_mappings.some(mapping => 
          mapping.year === parseInt(assignment.year) &&
          (!formData.selectedDegreeId || mapping.degree_details_id === formData.selectedDegreeId)
        )
      );
    }

    // Filter by semester if selected
    if (assignment.semester) {
      filtered = filtered.filter(module => 
        module.semester === parseInt(assignment.semester)
      );
    }

    setFilteredModules(prev => ({ ...prev, [assignmentId]: filtered }));
  };

  // Handle module search for course assignments
  const handleModuleSearch = (assignmentId, query) => {
    const assignment = courseAssignments.find(a => a.id === assignmentId);
    
    setCourseAssignments(prev => 
      prev.map(assignment => 
        assignment.id === assignmentId 
          ? { ...assignment, moduleSearchQuery: query }
          : assignment
      )
    );
    
    // Get base filtered modules (filtered by degree, year, and semester)
    let baseFiltered = modulesList;
    
    // Filter by selected degree programme from main form
    if (formData.selectedDegreeId) {
      baseFiltered = baseFiltered.filter(module => 
        module.module_degree_mappings && 
        module.module_degree_mappings.some(mapping => 
          mapping.degree_details_id === formData.selectedDegreeId
        )
      );
    }

    // Filter by year if selected
    if (assignment.year) {
      baseFiltered = baseFiltered.filter(module => 
        module.module_degree_mappings && 
        module.module_degree_mappings.some(mapping => 
          mapping.year === parseInt(assignment.year) &&
          (!formData.selectedDegreeId || mapping.degree_details_id === formData.selectedDegreeId)
        )
      );
    }

    // Filter by semester if selected
    if (assignment.semester) {
      baseFiltered = baseFiltered.filter(module => 
        module.semester === parseInt(assignment.semester)
      );
    }

    // Apply search query filter
    if (query.trim() === '') {
      setFilteredModules(prev => ({ ...prev, [assignmentId]: baseFiltered }));
    } else {
      const searchFiltered = baseFiltered.filter(module => 
        module.module_name.toLowerCase().includes(query.toLowerCase()) ||
        module.module_code.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredModules(prev => ({ ...prev, [assignmentId]: searchFiltered }));
    }
    setShowModuleDropdowns(prev => ({ ...prev, [assignmentId]: true }));
  };

  // Handle module selection for course assignments
  const handleModuleSelect = (assignmentId, module) => {
    console.log('Selected module for assignment:', assignmentId, module);
    setCourseAssignments(prev => 
      prev.map(assignment => 
        assignment.id === assignmentId 
          ? { 
              ...assignment, 
              moduleName: module.module_name,
              moduleSearchQuery: `${module.module_name} (${module.module_code})`,
              moduleId: module.id 
            }
          : assignment
      )
    );
    setShowModuleDropdowns(prev => ({ ...prev, [assignmentId]: false }));
  };

  // Add new course assignment
  const addCourseAssignment = () => {
    const newId = Math.max(...courseAssignments.map(c => c.id), 0) + 1;
    setCourseAssignments(prev => [
      ...prev,
      {
        id: newId,
        year: '',
        semester: '',
        moduleId: '',
        moduleName: '',
        moduleSearchQuery: '',
        type: '',
        status: 'Active'
      }
    ]);
    // Initialize dropdown state for new assignment
    setFilteredModules(prev => ({ ...prev, [newId]: modulesList }));
  };

  // Remove course assignment
  const removeCourseAssignment = (id) => {
    if (courseAssignments.length > 1) {
      setCourseAssignments(prev => prev.filter(assignment => assignment.id !== id));
      // Clean up dropdown states
      setFilteredModules(prev => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });
      setShowModuleDropdowns(prev => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.fullName || !formData.email || !formData.password || !formData.studentId) {
      alert('Please fill in all required fields');
      return;
    }

    if (!formData.selectedDegreeId) {
      alert('Please select a degree programme');
      return;
    }

    if (!formData.selectedGroupId) {
      alert('Please select a group');
      return;
    }

    if (!formData.currentYear || !formData.currentSemester) {
      alert('Please select current year and semester');
      return;
    }

    // Validate course assignments if academic year is provided
    if (academicYear) {
      const incompleteAssignments = courseAssignments.filter(assignment => 
        assignment.year && assignment.semester && assignment.moduleId && assignment.type && assignment.status
      );
      
      if (courseAssignments.some(a => a.year || a.semester || a.moduleId || a.type || a.status) && incompleteAssignments.length === 0) {
        alert('Please complete all course assignment fields or remove incomplete entries');
        return;
      }
    }

    if (!currentUser) {
      alert('Please log in to add students');
      return;
    }

    try {
      setLoading(true);

      // Get admin's university_id
      let universityId = currentUser?.university_id;
      
      if (!universityId && currentUser?.id) {
        const { data: adminData, error: adminError } = await supabase
          .from('admins')
          .select('university_id')
          .eq('user_id', currentUser.id)
          .single();
        
        if (!adminError && adminData) {
          universityId = adminData.university_id;
        }
      }

      if (!universityId) {
        alert('Unable to determine your university. Please contact support.');
        return;
      }

      console.log('Adding student for university:', universityId);

      // Prepare student data for Supabase students table (without photo initially)
      const studentData = {
        name: formData.fullName,
        email: formData.email,
        password: formData.password,
        student_id: formData.studentId,
        contact_no: formData.contactNumber || null,
        dob: formData.dateOfBirth || null,
        gender: formData.gender || null,
        address: formData.address || null,
        degree_details_id: formData.selectedDegreeId,
        group_id: formData.selectedGroupId,
        current_year: parseInt(formData.currentYear),
        current_semester: parseInt(formData.currentSemester),
        enrollment_date: formData.enrollmentDate || null,
        active: formData.status === 'Active',
        photo_url: null, // Will be updated later if photo upload succeeds
        university_id: universityId,
        created_at: new Date().toISOString()
      };

      console.log('Student data being submitted to Supabase:', studentData);

      // Insert student data into Supabase first (fast operation)
      const { data: insertedStudent, error: insertError } = await supabase
        .from('students')
        .insert([studentData])
        .select()
        .single();

      if (insertError) {
        console.error('Error inserting student:', insertError);
        throw insertError;
      }

      console.log('Student created successfully:', insertedStudent);

      // Handle photo upload in background (non-blocking)
      if (formData.photo) {
        // Don't await this - let it run in background
        uploadPhotoInBackground(insertedStudent.id, formData.photo, formData.studentId);
      }

      // Handle course assignments in background (don't block the UI)
      if (courseAssignments.length > 0 && academicYear && courseAssignments.some(a => a.moduleId)) {
        // Don't await this - let it run in background
        insertCourseAssignmentsInBackground(insertedStudent.id, courseAssignments, academicYear);
      }

      alert('Student added successfully!');
      navigate('/add-student');
      
    } catch (error) {
      console.error('Error adding student:', error);
      alert('Error adding student: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{width: '100%', height: '100vh', position: 'relative', background: '#FFFBF5'}}>
      <Sidebar />
      <div style={{
        marginLeft: isMobile ? 0 : 250, 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100vh',
        padding: isMobile ? '80px 15px 20px 15px' : '0'
      }}>
        <Header />
        {loading ? (
          <div style={{
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '50vh',
            fontSize: 18,
            fontFamily: 'Outfit',
            gap: '20px'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '5px solid #f3f3f3',
              borderTop: '5px solid #F04915',
              borderRadius: '50%',
              animation: 'spin 2s linear infinite'
            }}></div>
            <div>Creating student...</div>
            <style jsx>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        ) : (
        <>
        <form onSubmit={handleSubmit}>
          {/* Add New Student Section */}
          <div style={{margin: isMobile ? '10px 0' : '20px 40px', position: 'relative'}}>
            <div style={{background: '#F04915', borderTopLeftRadius: 15, borderTopRightRadius: 15, padding: isMobile ? '15px' : '20px', color: 'white'}}>
              <h2 style={{fontSize: isMobile ? 20 : 24, fontFamily: 'Outfit', fontWeight: '700'}}>Add New Student</h2>
            </div>

            <div style={{background: '#FFFBF5', padding: isMobile ? '20px' : '40px'}}>
              <div style={{
                display: 'grid', 
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
                gap: isMobile ? '20px' : '40px'
              }}>
                {/* Left Column */}
                <div>
                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: isMobile ? 18 : 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Full Name <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <input
                      name="fullName"
                      type="text"
                      placeholder="Enter full name..."
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                      style={{
                        width: '100%', 
                        height: isMobile ? 54 : 49, 
                        padding: isMobile ? '0 16px' : '0 15px', 
                        borderRadius: 8, 
                        border: '1px solid #77838F', 
                        fontSize: 16, 
                        fontFamily: 'Outfit',
                        minHeight: '48px',
                        WebkitAppearance: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Email <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <input
                      name="email"
                      type="email"
                      placeholder="Enter email address..."
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Password <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <input
                      name="password"
                      type="password"
                      placeholder="Enter password..."
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Student ID <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <input
                      name="studentId"
                      type="text"
                      placeholder="Enter student ID..."
                      value={formData.studentId}
                      onChange={handleInputChange}
                      required
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Contact Number
                    </label>
                    <input
                      name="contactNumber"
                      type="tel"
                      placeholder="Enter contact number..."
                      value={formData.contactNumber}
                      onChange={handleInputChange}
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Date of Birth
                    </label>
                    <input
                      name="dateOfBirth"
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Gender
                    </label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                {/* Right Column */}
                <div>
                  <div style={{marginBottom: '20px'}}>
                    <label style={getLabelStyles()}>
                      Degree Programme <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <select
                      name="degreeProgramme"
                      value={formData.degreeProgramme}
                      onChange={handleInputChange}
                      required
                      style={getInputStyles()}
                    >
                      <option value="">Select Degree Programme</option>
                      {degreeProgrammes.map(programme => (
                        <option key={programme.id} value={programme.displayName}>
                          {programme.displayName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Group <span style={{color: '#F04915'}}>*</span>
                    </label>
                    <select
                      name="group"
                      value={formData.group}
                      onChange={handleInputChange}
                      required
                      disabled={!formData.selectedDegreeId}
                      style={{
                        ...getInputStyles(),
                        backgroundColor: !formData.selectedDegreeId ? '#f5f5f5' : 'white',
                        cursor: !formData.selectedDegreeId ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <option value="">
                        {!formData.selectedDegreeId ? 'Select a degree programme first' : 'Select Group'}
                      </option>
                      {filteredGroups.map(group => (
                        <option key={group.id} value={group.group_name}>
                          {group.group_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{
                    display: 'grid', 
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
                    gap: '20px', 
                    marginBottom: '20px'
                  }}>
                    <div>
                      <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                        Current Year <span style={{color: '#F04915'}}>*</span>
                      </label>
                      <select
                        name="currentYear"
                        value={formData.currentYear}
                        onChange={handleInputChange}
                        required
                        style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                      >
                        <option value="">Select Year</option>
                        <option value="1">Year 1</option>
                        <option value="2">Year 2</option>
                        <option value="3">Year 3</option>
                        <option value="4">Year 4</option>
                      </select>
                    </div>

                    <div>
                      <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                        Current Semester <span style={{color: '#F04915'}}>*</span>
                      </label>
                      <select
                        name="currentSemester"
                        value={formData.currentSemester}
                        onChange={handleInputChange}
                        required
                        style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                      >
                        <option value="">Select Semester</option>
                        <option value="1">Semester 1</option>
                        <option value="2">Semester 2</option>
                      </select>
                    </div>
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Enrollment Date
                    </label>
                    <input
                      name="enrollmentDate"
                      type="date"
                      value={formData.enrollmentDate}
                      onChange={handleInputChange}
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    />
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Status
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      style={{width: '100%', height: 49, padding: '0 15px', borderRadius: 8, border: '1px solid #77838F', fontSize: 16, fontFamily: 'Outfit'}}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>

                  <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                      Photo
                    </label>
                    <div style={{width: '100%', height: 120, border: '1px dashed #77838F', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative'}}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        style={{opacity: 0, position: 'absolute', width: '100%', height: '100%', cursor: 'pointer'}}
                      />
                      <div style={{color: '#77838F', fontSize: 16, fontFamily: 'Outfit', fontWeight: '400', textAlign: 'center'}}>
                        Click to upload photo
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address - Full Width */}
                <div style={{gridColumn: '1 / -1', marginTop: '20px'}}>
                  <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 20, fontFamily: 'Outfit', fontWeight: '500'}}>
                    Address
                  </label>
                  <textarea
                    name="address"
                    placeholder="Enter full address..."
                    value={formData.address}
                    onChange={handleInputChange}
                    rows="3"
                    style={{
                      width: '100%', 
                      padding: '15px', 
                      borderRadius: 8, 
                      border: '1px solid #77838F', 
                      fontSize: 16, 
                      fontFamily: 'Outfit',
                      resize: 'vertical',
                      minHeight: '80px'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Course Assignments Section */}
          <div style={{margin: isMobile ? '10px 0' : '20px 40px', position: 'relative'}}>
            <div style={{background: '#F04915', borderTopLeftRadius: 15, borderTopRightRadius: 15, padding: isMobile ? '15px' : '20px', color: 'white'}}>
              <h2 style={{fontSize: isMobile ? 20 : 24, fontFamily: 'Outfit', fontWeight: '700'}}>Course Assignments</h2>
            </div>

            <div style={{background: '#FFFBF5', padding: isMobile ? '20px' : '40px'}}>
              {/* Academic Year Input */}
              <div style={{marginBottom: '30px'}}>
                <label style={getLabelStyles()}>
                  Academic Year <span style={{color: '#F04915'}}>*</span>
                </label>
                <select
                  value={academicYear}
                  onChange={(e) => setAcademicYear(e.target.value)}
                  style={{
                    ...getInputStyles(),
                    width: isMobile ? '100%' : '300px'
                  }}
                >
                  <option value="">Select Academic Year</option>
                  <option value="2023-2024">2023-2024</option>
                  <option value="2024-2025">2024-2025</option>
                  <option value="2025-2026">2025-2026</option>
                  <option value="2026-2027">2026-2027</option>
                </select>
              </div>

              {courseAssignments.map((assignment, index) => (
                <div key={assignment.id} style={{
                  marginBottom: index === courseAssignments.length - 1 ? '0' : '30px',
                  padding: '20px',
                  border: '1px solid #E0E0E0',
                  borderRadius: 8,
                  background: 'white'
                }}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
                    <h3 style={{fontSize: 18, fontFamily: 'Outfit', fontWeight: '600', color: '#333', margin: 0}}>
                      Course Assignment {index + 1}
                    </h3>
                    {courseAssignments.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCourseAssignment(assignment.id)}
                        style={{
                          background: '#DC3545',
                          border: 'none',
                          borderRadius: 6,
                          color: 'white',
                          padding: '8px 12px',
                          fontSize: 14,
                          fontFamily: 'Outfit',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.background = '#C82333';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.background = '#DC3545';
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 2fr 1fr 1fr', gap: '20px', alignItems: 'end'}}>
                    {/* Year */}
                    <div>
                      <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 16, fontFamily: 'Outfit', fontWeight: '500'}}>
                        Year <span style={{color: '#F04915'}}>*</span>
                      </label>
                      <select
                        value={assignment.year}
                        onChange={(e) => handleCourseAssignmentChange(assignment.id, 'year', e.target.value)}
                        style={{
                          width: '100%',
                          height: 49,
                          padding: '0 15px',
                          borderRadius: 8,
                          border: '1px solid #77838F',
                          fontSize: 16,
                          fontFamily: 'Outfit',
                          backgroundColor: 'white'
                        }}
                      >
                        <option value="">Select Year</option>
                        <option value="1">Year 1</option>
                        <option value="2">Year 2</option>
                        <option value="3">Year 3</option>
                        <option value="4">Year 4</option>
                      </select>
                    </div>

                    {/* Semester */}
                    <div>
                      <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 16, fontFamily: 'Outfit', fontWeight: '500'}}>
                        Semester <span style={{color: '#F04915'}}>*</span>
                      </label>
                      <select
                        value={assignment.semester}
                        onChange={(e) => handleCourseAssignmentChange(assignment.id, 'semester', e.target.value)}
                        style={{
                          width: '100%',
                          height: 49,
                          padding: '0 15px',
                          borderRadius: 8,
                          border: '1px solid #77838F',
                          fontSize: 16,
                          fontFamily: 'Outfit',
                          backgroundColor: 'white'
                        }}
                      >
                        <option value="">Select Semester</option>
                        <option value="1">Semester 1</option>
                        <option value="2">Semester 2</option>
                      </select>
                    </div>

                    {/* Module */}
                    <div style={{position: 'relative'}} className="module-search-container">
                      <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 16, fontFamily: 'Outfit', fontWeight: '500'}}>
                        Module <span style={{color: '#F04915'}}>*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Search for module..."
                        value={assignment.moduleSearchQuery}
                        onChange={(e) => handleModuleSearch(assignment.id, e.target.value)}
                        onFocus={() => {
                          setShowModuleDropdowns(prev => ({ ...prev, [assignment.id]: true }));
                          if (!filteredModules[assignment.id]) {
                            setFilteredModules(prev => ({ ...prev, [assignment.id]: modulesList }));
                          }
                        }}
                        style={{
                          width: '100%',
                          height: 49,
                          padding: '0 15px',
                          borderRadius: 8,
                          border: assignment.moduleId ? '2px solid #28a745' : '1px solid #77838F',
                          fontSize: 16,
                          fontFamily: 'Outfit',
                          backgroundColor: assignment.moduleId ? '#f8fff8' : 'white'
                        }}
                      />
                      {assignment.moduleId && (
                        <div style={{
                          fontSize: 12,
                          color: '#28a745',
                          marginTop: '2px',
                          fontFamily: 'Outfit'
                        }}>
                          ✓ Selected: {assignment.moduleName}
                        </div>
                      )}
                      {showModuleDropdowns[assignment.id] && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          background: 'white',
                          border: '1px solid #77838F',
                          borderTop: 'none',
                          borderRadius: '0 0 8px 8px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          zIndex: 1000,
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}>
                          {(filteredModules[assignment.id] || []).length === 0 ? (
                            <div style={{
                              padding: '12px 15px',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              color: '#888',
                              textAlign: 'center'
                            }}>
                              No modules found for your university
                            </div>
                          ) : (
                            (filteredModules[assignment.id] || []).map(module => (
                              <div
                                key={module.id}
                                onClick={() => handleModuleSelect(assignment.id, module)}
                                style={{
                                  padding: '10px 15px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f0f0f0',
                                  fontSize: 14,
                                  fontFamily: 'Outfit',
                                  transition: 'background-color 0.2s',
                                  backgroundColor: assignment.moduleId === module.id ? '#e3f2fd' : 'white'
                                }}
                                onMouseOver={(e) => {
                                  e.target.style.backgroundColor = '#f8f9fa';
                                }}
                                onMouseOut={(e) => {
                                  e.target.style.backgroundColor = assignment.moduleId === module.id ? '#e3f2fd' : 'white';
                                }}
                              >
                                {module.module_name} ({module.module_code})
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>

                    {/* Type */}
                    <div>
                      <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 16, fontFamily: 'Outfit', fontWeight: '500'}}>
                        Type <span style={{color: '#F04915'}}>*</span>
                      </label>
                      <select
                        value={assignment.type}
                        onChange={(e) => handleCourseAssignmentChange(assignment.id, 'type', e.target.value)}
                        style={{
                          width: '100%',
                          height: 49,
                          padding: '0 15px',
                          borderRadius: 8,
                          border: '1px solid #77838F',
                          fontSize: 16,
                          fontFamily: 'Outfit',
                          backgroundColor: 'white'
                        }}
                      >
                        <option value="">Select Type</option>
                        <option value="regular">Regular</option>
                        <option value="elective">Elective</option>
                        <option value="repeat">Repeat</option>
                      </select>
                    </div>

                    {/* Status */}
                    <div>
                      <label style={{display: 'block', marginBottom: '8px', color: 'black', fontSize: 16, fontFamily: 'Outfit', fontWeight: '500'}}>
                        Status <span style={{color: '#F04915'}}>*</span>
                      </label>
                      <select
                        value={assignment.status}
                        onChange={(e) => handleCourseAssignmentChange(assignment.id, 'status', e.target.value)}
                        style={{
                          width: '100%',
                          height: 49,
                          padding: '0 15px',
                          borderRadius: 8,
                          border: '1px solid #77838F',
                          fontSize: 16,
                          fontFamily: 'Outfit',
                          backgroundColor: 'white'
                        }}
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}

              {/* Add Course Button */}
              <div style={{marginTop: '20px', textAlign: 'center'}}>
                <button
                  type="button"
                  onClick={addCourseAssignment}
                  style={{
                    background: '#28A745',
                    border: 'none',
                    borderRadius: 8,
                    color: 'white',
                    padding: '12px 24px',
                    fontSize: 16,
                    fontFamily: 'Outfit',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    margin: '0 auto'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = '#218838';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = '#28A745';
                  }}
                >
                  <span style={{fontSize: '18px'}}>+</span>
                  Add Course
                </button>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{
            margin: isMobile ? '20px 0' : '40px', 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: isMobile ? 'stretch' : 'space-between',
            gap: isMobile ? '15px' : '0'
          }}>
            <Link to="/add-student">
              <button 
                type="button" 
                style={{
                  width: isMobile ? '100%' : 269, 
                  padding: isMobile ? '16px 20px' : '11px 20px', 
                  background: '#6C757D', 
                  border: 'none', 
                  borderRadius: 8, 
                  color: 'white', 
                  fontSize: isMobile ? 18 : 16, 
                  fontFamily: 'Outfit', 
                  fontWeight: '500', 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  minHeight: '48px'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#5A6268';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#6C757D';
                }}
              >
                Back
              </button>
            </Link>
            <button 
              type="submit" 
              disabled={loading}
              style={{
                width: isMobile ? '100%' : 269, 
                padding: isMobile ? '16px 20px' : '11px 20px', 
                background: loading ? '#cccccc' : '#F04915', 
                border: 'none', 
                borderRadius: 8, 
                color: 'white', 
                fontSize: isMobile ? 18 : 16, 
                fontFamily: 'Outfit', 
                fontWeight: '500', 
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                minHeight: '48px',
                marginTop: isMobile ? '10px' : '0'
              }}
              onMouseOver={(e) => {
                if (!loading) {
                  e.target.style.background = '#D63A0F';
                }
              }}
              onMouseOut={(e) => {
                if (!loading) {
                  e.target.style.background = '#F04915';
                }
              }}
            >
              {loading ? 'Creating...' : 'Submit'}
            </button>
          </div>
        </form>
        </>
        )}
      </div>
    </div>
  );
};

export default AddStudentForm;
